import apiClient from './client';

export type Client = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  company?: string;
  notes?: string;
  jobCount?: number;
  totalRevenue?: number;
  last_contact?: string;
};

export type ClientDocument = {
  id: number;
  name: string;
  url: string;
  type: string;
  size?: number;
  category?: string;
  uploaded_at?: string;
  caption?: string;
  file_path?: string;
  /** Set when the doc lives on one of the client's jobs (CR #121). */
  job_id?: number | null;
  job_title?: string | null;
};

export type ClientStats = {
  totalClients: number;
  newClientsThisMonth: number;
  activeJobs: number;
  totalRevenue: number;
  avgJobValue: number;
  revenueGrowth: number;
};

export const getClients = async (search?: string): Promise<Client[]> => {
  const response = await apiClient.get('/clients', { params: { search } });
  return response.data.data;
};

export const getClientStats = async (): Promise<ClientStats> => {
  const response = await apiClient.get('/clients/stats');
  return response.data.data;
};

export const getClientById = async (id: string): Promise<Record<string, unknown>> => {
  const response = await apiClient.get(`/clients/${id}`);
  return response.data.data;
};

export const createClient = async (clientData: Partial<Client>): Promise<Client> => {
  const response = await apiClient.post('/clients', clientData);
  return response.data.data;
};

export const updateClient = async (id: string, clientData: Partial<Client>): Promise<void> => {
  await apiClient.put(`/clients/${id}`, clientData);
};

export const deleteClient = async (id: string): Promise<void> => {
  await apiClient.delete(`/clients/${id}`);
};

// Document helpers (used by ClientDocumentsSection)
export const getClientDocuments = async (clientId: string): Promise<ClientDocument[]> => {
  const response = await apiClient.get(`/clients/${clientId}/documents`);
  const rows = (response.data.data || []) as Record<string, unknown>[];
  // Server rows use file_* columns; the section component wants name/url/type.
  return rows.map(r => ({
    id: Number(r.id),
    name: String(r.display_name || r.file_name || `File ${r.id}`),
    url: `/${String(r.file_path || '').replace(/^\//, '')}`,
    type: String(r.file_type || ''),
    size: r.file_size != null ? Number(r.file_size) : undefined,
    category: (r.document_category as string) || undefined,
    uploaded_at: r.uploaded_at as string | undefined,
    file_path: r.file_path as string | undefined,
    job_id: (r.job_id as number | null) ?? null,
    job_title: (r.job_title as string | null) ?? null,
  }));
};

export const uploadClientDocument = async (clientId: string, file: File, category?: string): Promise<any> => {
  const form = new FormData();
  form.append('file', file as any);
  if (category) form.append('category', category);
  const response = await apiClient.post(`/clients/${clientId}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
};

export const deleteClientDocument = async (clientId: string, documentId: string): Promise<void> => {
  await apiClient.delete(`/clients/${clientId}/documents/${documentId}`);
};
